import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import API_BASE from "../../config";
import ClassOfferingDetailModal from "./ClassOfferingDetailModal";
import ScheduleGrid from "./ScheduleGrid";
import { syLabel } from "../../utils/schoolYear";
import s from "./RoomDetailPage.module.css";

const AVATAR_COLORS = ["#5a4e3a","#3a4e4a","#4a3a5a","#4e4a3a","#3a4a5a","#5a3a3a","#3a5a3a"];
function avatarColor(seed) {
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}
function splitSchedule(raw) {
  if (!raw) return { days: "", time: "" };
  const idx = raw.lastIndexOf(" ");
  if (idx === -1) return { days: raw, time: "" };
  return { days: raw.slice(0, idx), time: raw.slice(idx + 1) };
}
function pillClassFor(roomType) {
  switch ((roomType || "").toUpperCase()) {
    case "LAB":     return s.typePillLab;
    case "MUSIC":   return s.typePillMusic;
    case "GYM":     return s.typePillGym;
    case "LIBRARY": return s.typePillLibrary;
    default:        return null;
  }
}

/**
 * Standalone "room hub" page reachable at /academics/rooms/:id. Lists
 * every class offering scheduled in this room (active term) and every
 * section that uses it as homeroom. Read-only — admins mutate via the
 * Class Offerings / Sections tabs.
 */
export default function RoomDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const roomId = id ? Number(id) : null;

  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState("");
  const [room, setRoom]       = useState(null);

  const [schoolYears, setSchoolYears] = useState([]);
  const [semesters,   setSemesters]   = useState([]);
  const [subjects,    setSubjects]    = useState([]);
  const [offerings,   setOfferings]   = useState([]);
  const [sections,    setSections]    = useState([]);
  const [gradeLevels, setGradeLevels] = useState([]);
  const [employees,   setEmployees]   = useState([]);
  const [students,    setStudents]    = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [rooms,       setRooms]       = useState([]);

  const [activeTab, setActiveTab] = useState("classes"); // "classes" | "schedule" | "homerooms"
  const [activeOffering, setActiveOffering] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr("");
    Promise.all([
      fetch(`${API_BASE}/api/rooms/${roomId}`).then(r => r.ok ? r.json() : null),
      fetch(`${API_BASE}/api/school-years`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/semesters`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/subjects`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/class-offerings`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/sections`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/grade-levels`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/employees?all=true`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/students`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/enrollments`).then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/rooms`).then(r => r.ok ? r.json() : []),
    ]).then(([rm, sy, sem, subj, off, sec, gl, emp, stu, enr, allRooms]) => {
      if (cancelled) return;
      setRoom(rm || null);
      setSchoolYears(sy || []);
      setSemesters(sem || []);
      setSubjects(subj || []);
      setOfferings(off || []);
      setSections(sec || []);
      setGradeLevels(gl || []);
      setEmployees(emp || []);
      setStudents(stu || []);
      setEnrollments(enr || []);
      setRooms(allRooms || []);
    }).catch(e => {
      if (cancelled) return;
      setErr(e.message || "Failed to load room");
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [roomId]);

  // Active term — defaults to active SY's active semester.
  const activeSy = useMemo(
    () => schoolYears.find(y => y.active) || schoolYears[0] || null,
    [schoolYears]
  );
  const activeSem = useMemo(() => {
    if (!activeSy) return null;
    const inSy = semesters.filter(sm => String(sm.schoolYearId) === String(activeSy.id));
    return inSy.find(sm => sm.active) || inSy[0] || null;
  }, [activeSy, semesters]);

  // Offerings scheduled in THIS room for the active term.
  const offeringsHere = useMemo(() => {
    return (offerings || []).filter(o => {
      if (String(o.roomId) !== String(roomId)) return false;
      if (activeSem && String(o.semesterId) !== String(activeSem.id)) return false;
      return true;
    });
  }, [offerings, roomId, activeSem]);

  // Sections that use this room as homeroom.
  const homeroomSections = useMemo(
    () => (sections || []).filter(sec => String(sec.homeroomId) === String(roomId)),
    [sections, roomId]
  );

  // Stat-card values.
  const uniqueSubjects = useMemo(
    () => new Set(offeringsHere.map(o => o.subjectId)).size,
    [offeringsHere]
  );
  const uniqueSections = useMemo(
    () => new Set(offeringsHere.map(o => o.sectionId)).size,
    [offeringsHere]
  );
  const uniqueTeachers = useMemo(
    () => new Set(offeringsHere.map(o => o.teacherStaffId).filter(x => x != null)).size,
    [offeringsHere]
  );

  // Sort offerings by section then by start time for predictability.
  const sortedOfferings = useMemo(() => {
    const sectionsById = Object.fromEntries(sections.map(x => [x.id, x]));
    return [...offeringsHere].sort((a, b) => {
      const aSec = sectionsById[a.sectionId]?.name || "";
      const bSec = sectionsById[b.sectionId]?.name || "";
      const cmpSec = aSec.localeCompare(bSec);
      if (cmpSec !== 0) return cmpSec;
      return (a.schedule || "").localeCompare(b.schedule || "");
    });
  }, [offeringsHere, sections]);

  if (loading) {
    return <div className={s.page}><div className={s.loading}>Loading room…</div></div>;
  }
  if (err || !room) {
    return (
      <div className={s.page}>
        <div className={s.crumbs}>
          <Link to="/academics" className={s.crumbLink}>Academics</Link>
          <span className={s.crumbSep}>›</span>
          <span className={s.crumbCurrent}>Room not found</span>
        </div>
        <div className={s.empty}>
          <div className={s.emptyTitle}>Room not found</div>
          <div className={s.emptySub}>
            {err || "This room may have been deleted, or you may not have access."}
          </div>
        </div>
      </div>
    );
  }

  const subjectsById = Object.fromEntries(subjects.map(x => [x.id, x]));
  const sectionsById = Object.fromEntries(sections.map(x => [x.id, x]));
  const gradeById    = Object.fromEntries(gradeLevels.map(x => [x.id, x]));
  const employeesById= Object.fromEntries(employees.map(x => [x.id, x]));

  return (
    <div className={s.page}>
      <div className={s.crumbs}>
        <Link to="/academics" className={s.crumbLink}>Academics</Link>
        <span className={s.crumbSep}>›</span>
        <Link to="/academics" className={s.crumbLink}>Rooms</Link>
        <span className={s.crumbSep}>›</span>
        <span className={s.crumbCurrent}>{room.name}</span>
      </div>

      <header className={s.hero}>
        <div
          className={s.heroAvatar}
          style={{ background: avatarColor(room.name) }}
          aria-hidden="true">
          {initials(room.name)}
        </div>
        <div className={s.heroMain}>
          <div className={s.eyebrow}>Academics · Rooms</div>
          <h1 className={s.title}>{room.name}</h1>
          <div className={s.metaRow}>
            {room.roomType && (
              <span className={`${s.typePill} ${pillClassFor(room.roomType) || ""}`}>
                {room.roomType}
              </span>
            )}
            {room.building && <span className={s.metaPill}>{room.building}</span>}
            {room.floor    && <span className={s.metaPill}>Floor {room.floor}</span>}
            {room.capacity != null && (
              <span className={s.metaPill}>{room.capacity} seats</span>
            )}
            {(activeSy || activeSem) && (
              <span className={s.metaPill}>
                {activeSy?.label ? syLabel(activeSy.label) : ""}
                {activeSy?.label && activeSem?.label ? " · " : ""}
                {activeSem?.label || ""}
              </span>
            )}
          </div>
        </div>
        <div className={s.heroActions}>
          <button type="button" className={s.btnGhost} onClick={() => navigate(-1)}>
            ← Back
          </button>
        </div>
      </header>

      <div className={s.stats}>
        <div className={s.stat} style={{ animationDelay: "0ms" }}>
          <div className={`${s.statIcon} ${s.statIconAmber}`} aria-hidden="true">◇</div>
          <div className={s.statBody}>
            <div className={s.statValue}>{offeringsHere.length}</div>
            <div className={s.statLabel}>{offeringsHere.length === 1 ? "Class scheduled" : "Classes scheduled"}</div>
          </div>
        </div>
        <div className={s.stat} style={{ animationDelay: "60ms" }}>
          <div className={`${s.statIcon} ${s.statIconBlue}`} aria-hidden="true">◫</div>
          <div className={s.statBody}>
            <div className={s.statValue}>{uniqueSections}</div>
            <div className={s.statLabel}>{uniqueSections === 1 ? "Section" : "Sections"}</div>
          </div>
        </div>
        <div className={s.stat} style={{ animationDelay: "120ms" }}>
          <div className={`${s.statIcon} ${s.statIconGreen}`} aria-hidden="true">●</div>
          <div className={s.statBody}>
            <div className={s.statValue}>{uniqueSubjects}</div>
            <div className={s.statLabel}>{uniqueSubjects === 1 ? "Subject" : "Subjects"}</div>
          </div>
        </div>
        <div className={s.stat} style={{ animationDelay: "180ms" }}>
          <div className={`${s.statIcon} ${s.statIconRose}`} aria-hidden="true">★</div>
          <div className={s.statBody}>
            <div className={s.statValue}>{uniqueTeachers}</div>
            <div className={s.statLabel}>{uniqueTeachers === 1 ? "Teacher" : "Teachers"}</div>
          </div>
        </div>
      </div>

      <div className={s.tabBar} role="tablist" aria-label="Room views">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "classes"}
          className={`${s.tab} ${activeTab === "classes" ? s.tabActive : ""}`}
          onClick={() => setActiveTab("classes")}>
          Classes
          <span className={s.tabCount}>{offeringsHere.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "schedule"}
          className={`${s.tab} ${activeTab === "schedule" ? s.tabActive : ""}`}
          onClick={() => setActiveTab("schedule")}>
          Schedule
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "homerooms"}
          className={`${s.tab} ${activeTab === "homerooms" ? s.tabActive : ""}`}
          onClick={() => setActiveTab("homerooms")}>
          Homeroom for
          <span className={s.tabCount}>{homeroomSections.length}</span>
        </button>
      </div>

      {activeTab === "classes" && (
        <div className={s.panel} role="tabpanel" aria-label="Classes">
          <div className={s.panelHead}>
            <h2 className={s.panelTitle}>Classes scheduled here</h2>
            <span className={s.panelSub}>
              Click any class to see subject, teacher, and roster.
            </span>
          </div>
          {sortedOfferings.length === 0 ? (
            <div className={s.empty}>
              <div className={s.emptyTitle}>No classes scheduled in this room yet</div>
              <div className={s.emptySub}>
                Assign this room from the Class Offerings tab.
              </div>
            </div>
          ) : (
            <ul className={s.offerList}>
              {sortedOfferings.map((o, i) => {
                const subj = subjectsById[o.subjectId];
                const sec  = sectionsById[o.sectionId];
                const grade = sec?.gradeLevelId ? gradeById[sec.gradeLevelId] : null;
                const tea  = o.teacherStaffId ? employeesById[o.teacherStaffId] : null;
                const { days, time } = splitSchedule(o.schedule);
                const delay = Math.min(i * 28, 420);
                return (
                  <li key={o.id}>
                    <button
                      type="button"
                      className={s.offerRow}
                      style={{ animationDelay: `${delay}ms` }}
                      onClick={() => setActiveOffering(o)}>
                      <div className={s.offerCode}>{subj?.code || "?"}</div>
                      <div className={s.offerMain}>
                        <div className={s.offerSubject}>{subj?.title || "—"}</div>
                        <div className={s.offerSection}>
                          {sec ? (grade ? `${grade.name} · ${sec.name}` : sec.name) : "—"}
                        </div>
                      </div>
                      <div className={`${s.offerTeacher} ${tea ? "" : s.offerTeacherWarn}`}>
                        {tea?.name || "No teacher"}
                      </div>
                      <div className={s.offerDays}>{days || "—"}</div>
                      <div className={s.offerTime}>{time || "—"}</div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {activeTab === "schedule" && (
        <div className={s.panel} role="tabpanel" aria-label="Schedule">
          <div className={s.panelHead}>
            <h2 className={s.panelTitle}>Schedule</h2>
            <span className={s.panelSub}>
              Room utilization across Mon–Fri. Click any block to drill in.
            </span>
          </div>
          <ScheduleGrid
            offerings={offeringsHere}
            subjects={subjects}
            sections={sections}
            employees={employees}
            rooms={rooms}
            gradeLevels={gradeLevels}
            colorBy="section"
            primaryFor="room"
            onBlockClick={setActiveOffering}
          />
        </div>
      )}

      {activeTab === "homerooms" && (
        <div className={s.panel} role="tabpanel" aria-label="Homeroom for">
          <div className={s.panelHead}>
            <h2 className={s.panelTitle}>Sections using this as homeroom</h2>
          </div>
          {homeroomSections.length === 0 ? (
            <div className={s.empty}>
              <div className={s.emptyTitle}>No sections use this as their homeroom</div>
              <div className={s.emptySub}>
                Assign this room as a homeroom from the Sections tab.
              </div>
            </div>
          ) : (
            <ul className={s.sectionList}>
              {homeroomSections.map(sec => {
                const grade = sec.gradeLevelId ? gradeById[sec.gradeLevelId] : null;
                return (
                  <li key={sec.id}>
                    <Link to={`/academics/sections/${sec.id}`} className={s.sectionRow}>
                      <div
                        className={s.sectionAvatar}
                        style={{ background: avatarColor(sec.name) }}
                        aria-hidden="true">
                        {initials(sec.name)}
                      </div>
                      <div>
                        <div className={s.sectionName}>{sec.name}</div>
                        <div className={s.sectionMeta}>
                          {grade ? grade.name : "—"}
                        </div>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      <ClassOfferingDetailModal
        open={!!activeOffering}
        offering={activeOffering}
        subjects={subjects}
        sections={sections}
        gradeLevels={gradeLevels}
        employees={employees}
        schoolYears={schoolYears}
        semesters={semesters}
        rooms={rooms}
        offerings={offerings}
        students={students}
        enrollments={enrollments}
        onClose={() => setActiveOffering(null)}
      />
    </div>
  );
}
